import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TextField,
    CircularProgress,
    Button
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/user';
import { Check, Close as X } from '@mui/icons-material';
import { CiClock2 } from 'react-icons/ci';

const ViewTestResult = () => {
    const [data, setData] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [fetchingData, setFetchingData] = useState(true);
    const [score, setScore] = useState('');
    const user = JSON.parse(localStorage.lasmabObj) || {};
    const token = localStorage.lasmabAuthToken || '';
    const { subject: encodedSubject, subjectId, title: encodedTitle, testId, duration, myScore } = useParams();

    const subject = decodeURIComponent(encodedSubject || '');
    const title = decodeURIComponent(encodedTitle || '');

    const navigate = useNavigate();

    const getTestResult = useCallback(async () => {
        try {
            setFetchingData(true);
            const res = await api.ViewCBTSubmission(
                token,
                testId
            );

            if (res.status && res.statusCode === 2) {
                setData(res.data || []);
                setScore(res.myScore || '');
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFetchingData(false);
        }
    }, [token, user.schoolId, user.classId, subjectId, testId]);

    useEffect(() => {
        getTestResult();
    }, [getTestResult]);

    const handleQuestionNavigation = (index) => {
        setCurrentQuestionIndex(index);
    };

    const currentQuestion = data[currentQuestionIndex] || {};
    const totalQuestions = data.length;
    const options = currentQuestion.options || {};

    // Helper to parse option strings like "A,B,C" or single-letter values
    const parseOptionSet = (val) => {
        if (!val && val !== '') return new Set();
        const str = Array.isArray(val) ? val.join(',') : String(val || '');
        return new Set(
            str
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
        );
    };

    // The API sometimes returns `correctOptions` / `selectedOptions` (comma list)
    // and sometimes `correctOption` / `selectedOption` (single or comma). Handle both.
    const correctSet = parseOptionSet(currentQuestion.correctOptions || currentQuestion.correctOption);
    const selectedSet = parseOptionSet(currentQuestion.selectedOptions || currentQuestion.selectedOption);

    const answeredQuestions = data.filter(
        (q) => q.selectedOption || q.selectedAnswer
    ).length;
    const correctAnswers = data.filter((q) => q.isAnswerCorrect === true).length;

    if (fetchingData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={60} sx={{ color: '#671E78' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <div
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 font-medium text-sm cursor-pointer hover:underline"
            >
                ← Go Back
            </div>

            <Box sx={{ pb: 12 }}>
                {data.length > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            padding: '20px',
                            gap: '20px'
                        }}
                    >
                        {/* LEFT SECTION */}
                        <Box sx={{ flex: 2 }}>
                            <h1 className="font-bold text-2xl mb-4">Test Result</h1>
                            <div className="p-5 flex justify-between rounded-lg bg-gray-200 shadow-sm mb-8">
                                <div>
                                    <h1 className="font-bold text-lg">{subject}</h1>
                                    <div className="flex items-center gap-3 pt-12 text-sm">
                                        <span>
                                            <CiClock2 />
                                        </span>
                                        <span>{`${duration} mins` || '0 min'}</span>
                                    </div>
                                </div>

                                <div>
                                    <p className="font-bold text-lg">Total Questions: {totalQuestions}</p>
                                    <p className="px-6 py-1 text-sm mt-12 bg-white rounded-full">{title}</p>
                                </div>
                                <div className="text-gray-800 font-semibold text-sm text-center">
                                    Score: {myScore}
                                </div>
                            </div>

                            {/* QUESTION */}
                            {currentQuestion && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" sx={{ fontFamily: 'Gilroy-Medium', mb: 2 }}>
                                        {currentQuestionIndex + 1}. {currentQuestion.question}
                                    </Typography>

                                    {/* Attachments */}
                                    {currentQuestion.attachments &&
                                        currentQuestion.attachments.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                {currentQuestion.attachments.map((file, index) => (
                                                    <Box key={index} sx={{ mt: 1 }}>
                                                        <img
                                                            src={file.filePath}
                                                            alt={`attachment-${index}`}
                                                            style={{
                                                                height: '250px',
                                                                width: '100%',
                                                                borderRadius: '8px',
                                                                marginTop: '8px'
                                                            }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                    {/* Fill in the gap */}
                                    {currentQuestion.answerType === 'F' ? (
                                        <Box>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                value={currentQuestion.selectedAnswer || ''}
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#671E78'
                                                        }
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Gilroy-Regular',
                                                    mt: 1,
                                                    color: currentQuestion.isAnswerCorrect ? 'green' : 'red'
                                                }}
                                            >
                                                Correct Answer: {currentQuestion.correctAnswer}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontFamily: 'Gilroy-Regular', mb: 2, color: '#666' }}
                                            >
                                                {currentQuestion.answerType === 'M'
                                                    ? '[Multiple answers]'
                                                    : currentQuestion.answerType === 'S'
                                                        ? '[Single answer]'
                                                        : '[Fill in the gap]'}
                                            </Typography>
                                            <Box sx={{ ml: 2 }}>
                                                {['A', 'B', 'C', 'D', 'E'].map((option) =>
                                                    options[`option${option}`] ? (
                                                        <Box
                                                            key={option}
                                                            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                                        >
                                                            {correctSet.has(option) ? (
                                                                // Show check for any correct option
                                                                <Check sx={{ color: 'green', mr: 1 }} />
                                                            ) : selectedSet.has(option) ? (
                                                                // Selected but not correct -> show X
                                                                <X sx={{ color: 'red', mr: 1 }} />
                                                            ) : (
                                                                <span style={{ width: '24px' }} />
                                                            )}

                                                            <Typography
                                                                sx={{
                                                                    fontFamily: 'Gilroy-Regular',
                                                                    color: correctSet.has(option)
                                                                        ? 'green'
                                                                        : selectedSet.has(option)
                                                                            ? 'red'
                                                                            : 'inherit'
                                                                }}
                                                            >
                                                                {options[`option${option}`]}
                                                            </Typography>
                                                        </Box>
                                                    ) : null
                                                )}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* RIGHT SECTION */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                backgroundColor: 'rgba(103, 30, 120, 0.1)',
                                padding: '20px',
                                borderRadius: '10px',
                                marginTop: '46px',
                                justifyContent: 'space-between',
                                height: '100%'
                            }}
                        >
                            <Box>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        mb: 3,
                                        boxShadow: 'none',
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <Table>
                                        <TableBody>
                                            {Array.from({ length: Math.ceil(totalQuestions / 5) }).map((_, row) => (
                                                <TableRow
                                                    key={row}
                                                    sx={{ border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}
                                                >
                                                    {[0, 1, 2, 3, 4].map((col) => {
                                                        const questionNumber = row * 5 + col;
                                                        return questionNumber < totalQuestions ? (
                                                            <TableCell
                                                                key={col}
                                                                align="center"
                                                                sx={{
                                                                    border: '1px solid #e0e0e0',
                                                                    backgroundColor: '#ffffff',
                                                                    padding: '8px'
                                                                }}
                                                            >
                                                                <Button
                                                                    variant={
                                                                        questionNumber === currentQuestionIndex
                                                                            ? 'contained'
                                                                            : 'outlined'
                                                                    }
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        minHeight: 0,
                                                                        width: 30,
                                                                        height: 30,
                                                                        backgroundColor:
                                                                            questionNumber === currentQuestionIndex
                                                                                ? '#671E78'
                                                                                : 'transparent',
                                                                        color:
                                                                            questionNumber === currentQuestionIndex ? '#fff' : '#000',
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                questionNumber === currentQuestionIndex
                                                                                    ? '#4e1661'
                                                                                    : 'rgba(103, 30, 120, 0.1)'
                                                                        }
                                                                    }}
                                                                    onClick={() => handleQuestionNavigation(questionNumber)}
                                                                >
                                                                    {questionNumber + 1}
                                                                </Button>
                                                            </TableCell>
                                                        ) : (
                                                            <TableCell
                                                                key={col}
                                                                sx={{
                                                                    border: '1px solid #e0e0e0',
                                                                    backgroundColor: '#ffffff'
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'Gilroy-Regular', textAlign: 'center' }}
                                    >
                                        Answered Questions
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'Gilroy-Medium',
                                            textAlign: 'center',
                                            fontSize: '22px',
                                            color: 'green'
                                        }}
                                    >
                                        {totalQuestions} of {totalQuestions}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'Gilroy-Regular', textAlign: 'center', mt: 2 }}
                                    >
                                        Correct Answers
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'Gilroy-Medium',
                                            textAlign: 'center',
                                            fontSize: '22px',
                                            color: correctAnswers === totalQuestions ? 'green' : 'orange'
                                        }}
                                    >
                                        {correctAnswers} of {totalQuestions}
                                    </Typography>
                                </Box>

                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <div>
                        <h1 className="font-bold text-2xl mb-4">Test Result</h1>
                        <div className="p-5 flex justify-between rounded-lg bg-gray-200 shadow-sm mb-8">
                            <div>
                                <h1 className="font-bold text-lg">{subject}</h1>
                            </div>
                            <div>
                                <p className="font-bold text-lg">Total Questions: {totalQuestions}</p>
                                <p className="px-6 py-1 text-sm mt-12 bg-white rounded-full">{title}</p>
                            </div>
                        </div>
                        <p className="text-center mt-7">No Result Available</p>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default ViewTestResult;
